import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Grid, Box, Container, Divider, Button } from '@mui/material'

// Types
import type { FooterProps } from '@/types/components/organisms/footer'

// Configurations
import Config from '@/config'

// Utils
import { localizeHref } from '@/utils/theme/nav-menu'

// Components
const TextButtonAtom = dynamic(() => import('@/components/atoms/button/text'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const ParagraphTypographyAtom = dynamic(() => import('@/components/atoms/typography/paragraph'))
const RIconAtom = dynamic(() => import('@/components/atoms/icon/r'))
const FooterLinksColumnMolecule = dynamic(() => import('@/components/molecules/footer/links-column'))
const LanguageSwitcherMolecule = dynamic(() => import('@/components/molecules/language/switcher'))

const FooterOrganism = (props: FooterProps) => {
	// Props
	const { lng, locale } = props

	// Variables
	// One column per Solutions group and per header dropdown
	const columns = [...locale.common.solutions.groups, ...locale.common.navDropdowns].map(group => ({
		title: group.title,
		// No "in the oven" tag here: the footer keeps one clean line per link; the tag shows in the header menu
		links: group.items.map(item => ({ title: item.title, href: localizeHref(lng, item.href) }))
	}))

	return (
		<Box
			component="footer"
			sx={{
				mt: { xs: 5, md: 10 },
				mx: { xs: 1, md: 4 },
				py: { xs: 2, md: 4 },
				bgcolor: 'common.white',
				borderTopLeftRadius: { xs: 32, md: 64 },
				borderTopRightRadius: { xs: 32, md: 64 }
			}}
		>
			<Container maxWidth="lg">
				{/* Logo and action buttons */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row' },
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 2,
						my: { xs: 1, md: 2 }
					}}
				>
					<Link href={`/${lng}`}>
						<TextButtonAtom text={locale.common.app.name} size="large" startIcon={<RIconAtom />} />
					</Link>

					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Link href={Config.appUrl}>
							<Button
								sx={{
									color: 'text.primary',
									fontSize: 15,
									fontWeight: 600,
									textTransform: 'inherit',
									px: 2,
									py: 0.75,
									borderRadius: 2,
									'&:hover': { bgcolor: '#f5f5f7', color: 'grey.600' }
								}}
							>
								{locale.links.login}
							</Button>
						</Link>

						<Link href={`/${lng}/signup`}>
							<PrimaryButtonAtom text={locale.links.requestDemo} color="primary" size="small" sx={{ px: 2.5, fontSize: 15 }} />
						</Link>
					</Box>
				</Box>

				<Divider sx={{ my: { xs: 2, md: 3 } }} />

				{/* Link columns */}
				<Grid container spacing={{ xs: 3, md: 2 }}>
					{columns.map(column => (
						<Grid key={column.title} size={{ xs: 6, sm: 4, md: 2 }}>
							<FooterLinksColumnMolecule {...column} />
						</Grid>
					))}
				</Grid>

				<Divider sx={{ mt: { xs: 3, md: 4 }, mb: 2 }} />

				{/* Copyright and language switch: the very last row */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column-reverse', sm: 'row' },
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 1,
						'& .MuiTypography-root': { fontSize: 13, color: 'grey.600' }
					}}
				>
					<ParagraphTypographyAtom text={locale.common.landing.copyright} align="left" />

					<LanguageSwitcherMolecule lng={lng} labels={locale.common.languages} />
				</Box>
			</Container>
		</Box>
	)
}

export default FooterOrganism
